import { useCallback, useEffect, useState } from 'react';
import { fetchSportsEvents } from '../services/api';
import { Event } from '../types';
import { sortEvents } from '../utils/eventUtils';

const REFRESH_INTERVAL_MS = 60_000;

export const useEvents = (autoRefresh = true) => {
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const refresh = useCallback(async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            const data = await fetchSportsEvents();
            setEvents(sortEvents(data));
            setError(null);
            setLastUpdated(new Date());
        } catch (err) {
            const message = err instanceof Error ? err.message : 'ইভেন্ট লোড করতে ব্যর্থ';
            setError(message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!autoRefresh) return undefined;

        const interval = window.setInterval(() => {
            refresh(true);
        }, REFRESH_INTERVAL_MS);

        return () => window.clearInterval(interval);
    }, [autoRefresh, refresh]);

    const updateEvent = useCallback((eventId: string, patch: Partial<Event>) => {
        setEvents((prev) =>
            sortEvents(prev.map((e) => (e.id === eventId ? { ...e, ...patch } : e)))
        );
    }, []);

    return {
        events,
        loading,
        refreshing,
        error,
        lastUpdated,
        refresh: () => refresh(true),
        updateEvent,
    };
};
